import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './CoachCheckoutModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

function CheckoutForm({ programId, programName, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError('');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Complete enrollment on backend
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/fitness/programs/${programId}/complete-enrollment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId: paymentIntent.id })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Enrollment failed');
        onSuccess(data);
      } catch (err) {
        setError(err.message);
      }
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <PaymentElement />
      {error && <p className="checkout-error">{error}</p>}
      <div className="checkout-actions">
        <button type="button" className="checkout-cancel" onClick={onClose} disabled={processing}>Cancel</button>
        <button type="submit" className="checkout-pay" disabled={!stripe || processing}>
          {processing ? 'Processing...' : `Pay for ${programName}`}
        </button>
      </div>
    </form>
  );
}

export default function CoachCheckoutModal({ coachAffiliateId, amount, currency, programName, programId, onSuccess, onClose }) {
  const [clientSecret, setClientSecret] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const createPayment = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/stripe-connect/create-payment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ coachAffiliateId, amount, currency, programId, programName })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create payment');
        setClientSecret(data.clientSecret);
      } catch (err) {
        setLoadError(err.message);
      }
    };
    createPayment();
  }, [coachAffiliateId, amount, currency, programId, programName]);

  if (!stripePromise) {
    return (
      <div className="checkout-overlay" onClick={onClose}>
        <div className="checkout-modal" onClick={e => e.stopPropagation()}>
          <p className="checkout-error">Stripe is not configured. Please contact support.</p>
          <button className="checkout-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-overlay" onClick={onClose}>
      <div className="checkout-modal" onClick={e => e.stopPropagation()}>
        <h3>Checkout — {programName}</h3>
        <p className="checkout-amount">${(amount / 100).toFixed(2)} {(currency || 'usd').toUpperCase()}</p>

        {loadError && <p className="checkout-error">{loadError}</p>}

        {clientSecret ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm programId={programId} programName={programName} onSuccess={onSuccess} onClose={onClose} />
          </Elements>
        ) : !loadError ? (
          <p className="checkout-loading">Setting up payment...</p>
        ) : null}
      </div>
    </div>
  );
}
